import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Paper,
    IconButton,
    Typography,
    Box,
    Avatar,
    alpha,
    CircularProgress,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { Close as CloseIcon, Assignment as AssignmentIcon, Person as PersonIcon, Comment as CommentIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

const burgundyColor = '#800020';

const trafficLightColors = {
    APPROVED: {
        main: '#2E7D32', // Professional forest green
        light: '#A5D6A7',
        dark: '#1B5E20',
        gradient: 'linear-gradient(135deg, #2E7D32, #1B5E20)',
        glow: '0 0 20px rgba(46, 125, 50, 0.2)'
    },
    DECLINED: {
        main: '#C62828', // Deep professional red
        light: '#EF9A9A',
        dark: '#B71C1C',
        gradient: 'linear-gradient(135deg, #C62828, #B71C1C)',
        glow: '0 0 20px rgba(198, 40, 40, 0.2)'
    },
    PENDING: {
        main: '#F57C00', // Professional orange
        light: '#FFB74D',
        dark: '#E65100',
        gradient: 'linear-gradient(135deg, #F57C00, #E65100)',
        glow: '0 0 20px rgba(245, 124, 0, 0.2)'
    }
};

const TimelineCard = ({ title, content, icon, color = 'APPROVED' }) => (
    <Paper
        elevation={4}
        sx={{
            p: 2,
            borderRadius: 2,
            background: 'white',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            boxShadow: `0 4px 12px ${alpha(trafficLightColors[color].main, 0.2)}`,
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: trafficLightColors[color].glow
            },
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: trafficLightColors[color].gradient
            }
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
                sx={{
                    bgcolor: alpha(trafficLightColors[color].main, 0.1),
                    color: trafficLightColors[color].main,
                    mr: 1.5,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'rotate(360deg)',
                        bgcolor: trafficLightColors[color].main,
                        color: 'white'
                    }
                }}
            >
                {icon}
            </Avatar>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    // Change color based on status
                    color: title.includes('PENDING') ? burgundyColor : trafficLightColors[color]?.main || '#40565C',
                    textShadow: `0 0 1px ${alpha(trafficLightColors[color]?.main || '#40565C', 0.1)}`
                }}
            >
                {title}
            </Typography>
        </Box>
        {content}
    </Paper>
);

TimelineCard.propTypes = {
    title: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired,
    icon: PropTypes.node.isRequired,
    color: PropTypes.string
};

export const AcquittalTimelineDialog = ({ open, handleClose, timeline, loading }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();

        const hour = date.getHours().toString().padStart(2, '0');
        const minute = date.getMinutes().toString().padStart(2, '0');

        const formattedDateTime = `${day} ${month} ${year}, ${hour}:${minute}`;

        return formattedDateTime;
    };

    const getTimelineItems = () =>
        timeline?.map((status) => ({
            title: `Stage: ${status.approvalLevel.replace('ROLE_', '')}`,
            content: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Approver Information */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: trafficLightColors[status.status].dark
                        }}
                    >
                        <PersonIcon fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {status.approvedBy ? `${status.approvedBy.firstName} ${status.approvedBy.lastName}` : 'Pending Approval'}
                        </Typography>
                    </Box>

                    {/* Status and Remarks Container */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(trafficLightColors[status.status].main, 0.05),
                            border: `1px solid ${alpha(trafficLightColors[status.status].main, 0.1)}`
                        }}
                    >
                        {/* Status Badge */}
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                bgcolor: alpha(trafficLightColors[status.status].main, 0.1),
                                color: trafficLightColors[status.status].main,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                mb: status.remarks ? 2 : 0
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Status: {status.status}
                            </Typography>
                        </Box>

                        {/* Remarks Section */}
                        {status.remarks && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CommentIcon fontSize="small" sx={{ color: trafficLightColors[status.status].main }} />
                                    <Typography variant="subtitle2" sx={{ color: trafficLightColors[status.status].dark, fontWeight: 600 }}>
                                        Remarks
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.6,
                                        maxHeight: '150px',
                                        overflowY: 'auto',
                                        backgroundColor: 'white',
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: `1px solid ${alpha(trafficLightColors[status.status].main, 0.1)}`,
                                        '&::-webkit-scrollbar': {
                                            width: '8px'
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: alpha(trafficLightColors[status.status].main, 0.05)
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: alpha(trafficLightColors[status.status].main, 0.2),
                                            borderRadius: '4px',
                                            '&:hover': {
                                                background: alpha(trafficLightColors[status.status].main, 0.3)
                                            }
                                        }
                                    }}
                                >
                                    {status.remarks}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            ),
            icon: <AssignmentIcon />,
            color: status.status,
            date: formatDate(status.approvedAt || status.createdAt)
        })) || [];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    bgcolor: '#FDFDFD',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: trafficLightColors.APPROVED.gradient,
                    color: 'white',
                    py: isMobile ? 2 : 3
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, color: 'white' }}>
                        Acquittal Timeline
                    </Typography>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: isMobile ? 2 : 4 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Timeline position={isMobile ? 'right' : 'alternate'} sx={{ p: isMobile ? 0 : 2 }}>
                        {getTimelineItems().map((item, index) => (
                            <TimelineItem key={index}>
                                <TimelineSeparator>
                                    <TimelineDot
                                        sx={{
                                            bgcolor: trafficLightColors[item.color].main,
                                            p: isMobile ? 1 : 1.5
                                        }}
                                    >
                                        {React.cloneElement(item.icon, { sx: { fontSize: isMobile ? 18 : 24 } })}
                                    </TimelineDot>
                                    {index < getTimelineItems().length - 1 && (
                                        <TimelineConnector
                                            sx={{
                                                background: trafficLightColors[item.color].gradient
                                            }}
                                        />
                                    )}
                                </TimelineSeparator>
                                <TimelineContent sx={{ px: isMobile ? 1 : 2, py: isMobile ? 1 : 2 }}>
                                    <TimelineCard title={item.title} content={item.content} icon={item.icon} color={item.color} />
                                    <Box sx={{ mt: 1, textAlign: isMobile || index % 2 === 0 ? 'right' : 'left' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'inline-block',
                                                px: 1,
                                                py: 0.5,
                                                bgcolor: alpha(trafficLightColors[item.color].main, 0.1),
                                                borderRadius: 1,
                                                color: trafficLightColors[item.color].main,
                                                fontWeight: 600
                                            }}
                                        >
                                            {item.date}
                                        </Typography>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </Timeline>
                )}
            </DialogContent>
        </Dialog>
    );
};

AcquittalTimelineDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    timeline: PropTypes.array,
    loading: PropTypes.bool
};

export default AcquittalTimelineDialog;
